import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PhotoHealthCard from "@/components/PhotoHealthCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Building2,
    Users2,
    ShieldCheck,
    Plus,
    Pencil,
    Trash2,
    Calendar,
    CheckCircle2,
    XCircle,
    Sheet as SheetIcon,
    RefreshCw,
    Send,
    LogOut,
    Sparkles,
    Image,
    Wrench,
    ListOrdered,
    Database,
} from "lucide-react";

function fmtErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
    return d?.msg || String(d);
}

export default function AdminPlatform() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("districts");

    // Guard: super admin only
    if (user && user.role !== "super_admin") {
        navigate("/admin");
        return null;
    }

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-platform-page">
            <header className="sticky top-0 z-30 glass-panel">
                <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="min-w-0">
                            <p className="label-eyebrow leading-none">Super Admin</p>
                            <p className="rotary-heading truncate text-base leading-tight text-rotary-ink sm:text-lg">Platform Console</p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={handleLogout}
                        className="shrink-0 rounded-full"
                        size="sm"
                        data-testid="platform-logout-btn"
                    >
                        <LogOut className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Sign out</span>
                    </Button>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="mb-6 flex w-full flex-wrap gap-1 sm:w-auto">
                        <TabsTrigger value="districts" data-testid="tab-districts">
                            <Building2 className="mr-1.5 h-4 w-4" /> Districts
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users">
                            <Users2 className="mr-1.5 h-4 w-4" /> Users
                        </TabsTrigger>
                        <TabsTrigger value="audit" data-testid="tab-audit">
                            <ShieldCheck className="mr-1.5 h-4 w-4" /> Audit log
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="districts">
                        <DistrictsSection />
                    </TabsContent>
                    <TabsContent value="users">
                        <UsersSection />
                    </TabsContent>
                    <TabsContent value="audit">
                        <AuditLogSection />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

/* ============================== Districts ============================== */

function DistrictsSection() {
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null); // district being edited
    const [yearOpenForDistrict, setYearOpenForDistrict] = useState(null); // district id when create year
    const [editingYear, setEditingYear] = useState(null); // { district, year }

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/super/districts");
            setDistricts(data);
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail) || "Failed to load districts");
        } finally {
            setLoading(false);
        }
    };
    // Districts: fetch once on mount
    useEffect(() => { load(); }, []);

    const del = async (d) => {
        if (!window.confirm(`Delete district "${d.display_name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/super/districts/${d.id}`);
            toast.success("District deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail));
        }
    };

    const delYear = async (d, y) => {
        if (!window.confirm(`Delete year ${y.year} for ${d.display_name}?`)) return;
        try {
            await api.delete(`/super/districts/${d.id}/years/${y.id}`);
            toast.success("Year deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-6">
            <PhotoHealthCard />
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="rotary-heading text-2xl text-rotary-ink">Districts</h2>
                    <p className="text-sm text-muted-foreground">{districts.length} district{districts.length !== 1 && "s"} on the platform</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/admin/photos">
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="photo-dashboard-launch-btn">
                            <Image className="mr-1.5 h-4 w-4" /> Photos
                        </Button>
                    </Link>
                    <Link to="/admin/names">
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="name-review-launch-btn">
                            <Sparkles className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">Structured&nbsp;</span>names
                        </Button>
                    </Link>
                    <Link to="/admin/positions">
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="positions-manager-launch-btn">
                            <ListOrdered className="mr-1.5 h-4 w-4" /> Positions
                        </Button>
                    </Link>
                    <Link to="/admin/classification-backfill">
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="classification-backfill-launch-btn">
                            <Wrench className="mr-1.5 h-4 w-4" /> Classification&nbsp;fix
                        </Button>
                    </Link>
                    <Button variant="outline" size="sm" onClick={load} className="rounded-full" data-testid="districts-refresh-btn">
                        <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setCreateOpen(true)}
                        className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                        data-testid="districts-create-btn"
                    >
                        <Plus className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">New&nbsp;</span>district
                    </Button>
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : districts.length === 0 ? (
                <div className="rounded-xl border p-10 text-center">
                    <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm text-muted-foreground">No districts yet. Create the first one.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {districts.map((d) => (
                        <DistrictCard
                            key={d.id}
                            district={d}
                            onEdit={() => setEditing(d)}
                            onDelete={() => del(d)}
                            onCreateYear={() => setYearOpenForDistrict(d)}
                            onEditYear={(y) => setEditingYear({ district: d, year: y })}
                            onDeleteYear={(y) => delYear(d, y)}
                        />
                    ))}
                </div>
            )}

            <DistrictModal
                open={createOpen}
                onOpenChange={setCreateOpen}
                onSaved={() => { setCreateOpen(false); load(); }}
            />
            <DistrictModal
                open={!!editing}
                district={editing}
                onOpenChange={(o) => !o && setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
            />
            <YearModal
                open={!!yearOpenForDistrict}
                district={yearOpenForDistrict}
                onOpenChange={(o) => !o && setYearOpenForDistrict(null)}
                onSaved={() => { setYearOpenForDistrict(null); load(); }}
            />
            <YearModal
                open={!!editingYear}
                district={editingYear?.district}
                yearRow={editingYear?.year}
                onOpenChange={(o) => !o && setEditingYear(null)}
                onSaved={() => { setEditingYear(null); load(); }}
            />
        </div>
    );
}

function MigrateToRegistryButton({ district }) {
    const [busy, setBusy] = useState(false);
    const migrate = async () => {
        const yn = window.confirm(
            `Move personal fields for ${district.display_name} from year tabs into a single "Member Registry" tab?\n\n` +
            `• The year tabs will be rewritten as thin roles-only rows (S.No., Phone, Committee levels, Position).\n` +
            `• Every personal edit will go to Registry after this.\n` +
            `• Safe to re-run; existing Registry values are preserved.`,
        );
        if (!yn) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/scoped/districts/${district.id}/migrate-to-registry`);
            const total = data?.registry_total ?? 0;
            const tabs = Object.keys(data?.tabs || {}).length;
            toast.success(`Registry ready — ${total} members across ${tabs} tab${tabs === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Migration failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Button
            variant="ghost"
            size="sm"
            onClick={migrate}
            disabled={busy}
            title="Migrate this district's Google Sheet to the Member Registry architecture"
            data-testid={`district-migrate-registry-${district.district_number}`}
        >
            <Database className="h-4 w-4" />
        </Button>
    );
}


function DistrictCard({ district, onEdit, onDelete, onCreateYear, onEditYear, onDeleteYear }) {
    return (
        <div className="rounded-xl border bg-white p-5" data-testid={`district-card-${district.district_number}`}>
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="rotary-chip bg-rotary-blue text-white border-transparent">RID {district.district_number}</span>
                        <h3 className="rotary-heading text-xl text-rotary-ink">{district.display_name}</h3>
                    </div>
                    {district.tagline && <p className="mt-1 text-sm text-muted-foreground">{district.tagline}</p>}
                    <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-3">
                        {district.contact_email && <span>{district.contact_email}</span>}
                        {district.contact_phone && <span>{district.contact_phone}</span>}
                        <span>{district.years_count} year{district.years_count !== 1 && "s"}</span>
                    </div>
                </div>
                <div className="flex gap-1.5">
                    <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`district-edit-${district.district_number}`}>
                        <Pencil className="h-4 w-4" />
                    </Button>
                    <MigrateToRegistryButton district={district} />
                    {/* District delete removed — deletion is destructive and only supported via API by request. */}
                </div>
            </div>

                    <div className="mt-4 space-y-2">
                        {district.years.map((y) => (
                            <div
                                key={y.id}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-4 py-2.5"
                                data-testid={`year-row-${district.district_number}-${y.year}`}
                            >
                                <div className="flex items-center gap-3">
                                    <Calendar className="h-4 w-4 text-rotary-blue" />
                                    <div>
                                        <p className="rotary-heading text-sm text-rotary-ink leading-none">{y.year}{y.is_bootstrap && <span className="ml-2 text-[10px] font-normal text-muted-foreground">[legacy]</span>}</p>
                                        <p className="text-[11px] text-muted-foreground mt-1">
                                            Status: <span className={`font-medium ${y.status === "active" ? "text-emerald-700" : y.status === "closed" ? "text-muted-foreground" : "text-amber-700"}`}>{y.status}</span>
                                            {" · "}{y.member_count} members · {y.submitted_count} submitted
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`rotary-chip text-[11px] ${y.paid_status ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                                        title={y.paid_at ? `Paid ${new Date(y.paid_at).toLocaleDateString()}` : "Not paid"}
                                    >
                                        {y.paid_status ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                        {y.paid_status ? "Paid" : "Unpaid"}
                                    </span>
                                    {y.sheet_url && (
                                        <a href={y.sheet_url} target="_blank" rel="noreferrer" className="text-xs text-rotary-blue hover:underline flex items-center gap-1">
                                            <SheetIcon className="h-3 w-3" /> Sheet
                                        </a>
                                    )}
                                    <Link to={`/admin/year/${y.id}`} data-testid={`year-open-${district.district_number}-${y.year}`}>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="rounded-full border-rotary-blue/40 text-rotary-blue hover:bg-rotary-blue hover:text-white"
                                            title="Open the district admin workspace for this year (impersonation view)"
                                        >
                                            Open admin view
                                        </Button>
                                    </Link>
                                    <Button variant="ghost" size="sm" onClick={() => onEditYear(y)} data-testid={`year-edit-${district.district_number}-${y.year}`}>
                                        <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    {/* Year delete removed — deletion is destructive and only supported via API by request. */}
                                </div>
                            </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={onCreateYear} className="w-full mt-1" data-testid={`year-create-${district.district_number}`}>
                            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add year
                        </Button>
                    </div>
        </div>
    );
}

function DistrictModal({ open, onOpenChange, onSaved, district }) {
    const editing = !!district;
    const [form, setForm] = useState({
        district_number: "",
        display_name: "",
        tagline: "",
        contact_email: "",
        contact_phone: "",
        timezone: "Asia/Kolkata",
        master_sheet_url: "",
        // Iter 81: district-level default photo spec (blank strings mean
        // "unset → falls back to the built-in 224×224 default").
        photo_min_width: "",
        photo_min_height: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                district_number: district?.district_number || "",
                display_name: district?.display_name || "",
                tagline: district?.tagline || "",
                contact_email: district?.contact_email || "",
                contact_phone: district?.contact_phone || "",
                timezone: district?.timezone || "Asia/Kolkata",
                master_sheet_url: district?.master_sheet_url || "",
                photo_min_width: String(district?.photo_spec?.min_width || ""),
                photo_min_height: String(district?.photo_spec?.min_height || ""),
            });
        }
    }, [open, district]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Compose payload — split photo_min_* into a nested photo_spec.
            // Empty strings → send an empty object so the backend clears any
            // stored value; positive ints → send them as-is.
            const { district_number: _dn, photo_min_width, photo_min_height, ...rest } = form;
            const pw = Number(photo_min_width) > 0 ? Number(photo_min_width) : null;
            const ph = Number(photo_min_height) > 0 ? Number(photo_min_height) : null;
            const payload = {
                ...rest,
                photo_spec: {
                    min_width: pw ?? undefined,
                    min_height: ph ?? undefined,
                },
            };
            if (editing) {
                await api.patch(`/super/districts/${district.id}`, payload);
                toast.success("District updated");
            } else {
                await api.post("/super/districts", form);
                toast.success("District created");
            }
            onSaved?.();
        } catch (e2) {
            toast.error(fmtErr(e2?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl">{editing ? "Edit district" : "New district"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="label-eyebrow">District number *</Label>
                            <Input
                                value={form.district_number}
                                onChange={(e) => setForm({ ...form, district_number: e.target.value })}
                                placeholder="3192"
                                disabled={editing}
                                data-testid="district-number-input"
                                required
                            />
                        </div>
                        <div>
                            <Label className="label-eyebrow">Timezone</Label>
                            <Input
                                value={form.timezone}
                                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                                placeholder="Asia/Kolkata"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="label-eyebrow">Display name *</Label>
                        <Input
                            value={form.display_name}
                            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                            placeholder="RI District 3192 · Karnataka South"
                            data-testid="district-name-input"
                            required
                        />
                    </div>
                    <div>
                        <Label className="label-eyebrow">Tagline</Label>
                        <Input
                            value={form.tagline}
                            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                            placeholder="Rotary International District 3192"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="label-eyebrow">Contact email</Label>
                            <Input
                                type="email"
                                value={form.contact_email}
                                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                placeholder="secretariat@d3192.org"
                            />
                        </div>
                        <div>
                            <Label className="label-eyebrow">Contact phone</Label>
                            <Input
                                value={form.contact_phone}
                                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                                placeholder="10-digit number"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="label-eyebrow">Master Google Sheet workbook URL</Label>
                        <Input
                            className="mt-1"
                            value={form.master_sheet_url}
                            onChange={(e) => setForm({ ...form, master_sheet_url: e.target.value })}
                            placeholder="https://docs.google.com/spreadsheets/d/…"
                            data-testid="district-master-sheet-input"
                        />
                        <p className="mt-1 text-[11px] text-muted-foreground">
                            One workbook per district. Each Rotary year gets its own tab (named <code>2026-27</code>, <code>2027-28</code>, …) auto-created inside this workbook. Previous year tabs are never overwritten.
                        </p>
                    </div>
                    {/* Iter 81 · District default photo spec — falls back to 224×224 when both are blank. */}
                    <div className="rounded-lg border bg-muted/20 p-3">
                        <p className="label-eyebrow text-[11px]">Default photo spec</p>
                        <p className="text-[11px] text-muted-foreground mb-2">Minimum accepted upload dimensions. Applied to every year in this district unless a year overrides it. Leave blank to use the built-in 224×224 default.</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[11px]" htmlFor="district-photo-min-w">Min width (px)</Label>
                                <Input
                                    id="district-photo-min-w"
                                    type="number"
                                    min="0"
                                    value={form.photo_min_width}
                                    onChange={(e) => setForm({ ...form, photo_min_width: e.target.value })}
                                    placeholder="224"
                                    data-testid="district-photo-min-width-input"
                                />
                            </div>
                            <div>
                                <Label className="text-[11px]" htmlFor="district-photo-min-h">Min height (px)</Label>
                                <Input
                                    id="district-photo-min-h"
                                    type="number"
                                    min="0"
                                    value={form.photo_min_height}
                                    onChange={(e) => setForm({ ...form, photo_min_height: e.target.value })}
                                    placeholder="224"
                                    data-testid="district-photo-min-height-input"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="district-save-btn">
                            {saving ? "Saving…" : editing ? "Save changes" : "Create district"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function YearModal({ open, onOpenChange, onSaved, district, yearRow }) {
    const editing = !!yearRow;
    const [form, setForm] = useState({
        year: "",
        governor_name: "",
        theme_line: "",
        sheet_url: "",
        photos_folder_url: "",
        status: "draft",
        paid_status: false,
        governor_message: "",
        photo_min_width: "",   // iter 81 — optional year override
        photo_min_height: "",
    });
    const [saving, setSaving] = useState(false);
    const [provisioning, setProvisioning] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                year: yearRow?.year || suggestNextYear(district?.years),
                governor_name: yearRow?.governor_name || "",
                theme_line: yearRow?.theme_line || "",
                sheet_url: yearRow?.sheet_url || "",
                photos_folder_url: yearRow?.photos_folder_url || "",
                status: yearRow?.status || "draft",
                paid_status: yearRow?.paid_status || false,
                governor_message: yearRow?.governor_message || "",
                photo_min_width: String(yearRow?.photo_spec?.min_width || ""),
                photo_min_height: String(yearRow?.photo_spec?.min_height || ""),
            });
        }
    }, [open, yearRow, district]);

    const submit = async (e) => {
        e.preventDefault();
        if (!district) return;
        setSaving(true);
        try {
            const { photo_min_width, photo_min_height, ...rest } = form;
            const pw = Number(photo_min_width) > 0 ? Number(photo_min_width) : null;
            const ph = Number(photo_min_height) > 0 ? Number(photo_min_height) : null;
            const payload = {
                ...rest,
                photo_spec: { min_width: pw ?? undefined, min_height: ph ?? undefined },
            };
            if (editing) {
                await api.patch(`/super/district-years/${yearRow.id}`, payload);
                toast.success("Year updated");
            } else {
                await api.post(`/super/districts/${district.id}/years`, form);
                toast.success(`Year ${form.year} created for ${district.display_name}`);
            }
            onSaved?.();
        } catch (e2) {
            toast.error(fmtErr(e2?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0">
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle className="rotary-heading text-2xl">
                        {editing ? `Edit year ${yearRow?.year}` : "Add district year"}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        {district?.display_name}
                    </p>
                </DialogHeader>
                <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
                    <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="label-eyebrow">Rotary year *</Label>
                            <Input
                                value={form.year}
                                onChange={(e) => setForm({ ...form, year: e.target.value })}
                                placeholder="2026-27"
                                data-testid="year-input"
                                required
                            />
                            {yearFormatError(form.year) && form.year && (
                                <p className="mt-1 text-[11px] text-rotary-cranberry">
                                    {yearFormatError(form.year)}
                                </p>
                            )}
                            {!yearFormatError(form.year) && yearRangeWarning(form.year) && (
                                <p className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                    ⚠ {yearRangeWarning(form.year)}
                                </p>
                            )}
                            <p className="mt-1 text-[11px] text-muted-foreground">
                                {editing
                                    ? "Renaming cascades to all member assignments and admin users for this year."
                                    : `Format: YYYY-YY (July–June). Current Rotary year: ${currentRotaryYear()}.`}
                            </p>
                        </div>
                        <div>
                            <Label className="label-eyebrow">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Draft (in prep)</SelectItem>
                                    <SelectItem value="active">Active (live)</SelectItem>
                                    <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="label-eyebrow">Governor name</Label>
                        <Input
                            value={form.governor_name}
                            onChange={(e) => setForm({ ...form, governor_name: e.target.value })}
                            placeholder="Rtn. Amit Sharma"
                        />
                    </div>
                    <div>
                        <Label className="label-eyebrow">Theme line</Label>
                        <Input
                            value={form.theme_line}
                            onChange={(e) => setForm({ ...form, theme_line: e.target.value })}
                            placeholder="Serve to Change Lives"
                        />
                    </div>
                    {editing && (
                        <div>
                            <Label className="label-eyebrow">Governor message</Label>
                            <Textarea
                                value={form.governor_message}
                                onChange={(e) => setForm({ ...form, governor_message: e.target.value })}
                                placeholder="Message from the governor…"
                                rows={3}
                            />
                        </div>
                    )}
                    <div className="rounded-lg border bg-muted/20 p-3 text-xs" data-testid="year-sheet-info">
                        <p className="label-eyebrow mb-1">Google Sheet</p>
                        <p className="text-muted-foreground leading-snug">
                            New architecture: a <strong>single master workbook</strong> is set at the district level, and each year gets its own tab named after the year (e.g. <code>2026-27</code>). The tab is auto-created when this year is saved.
                            {" "}
                            {form.tab_name && (
                                <> This year will use tab: <code className="text-rotary-blue">{form.tab_name}</code>.</>
                            )}
                        </p>
                    </div>
                    {/* Drive photos folder URL input removed in iter 47 —
                        image storage is now Emergent Object Storage only.
                        The DB field is retained for legacy district-years
                        but is no longer editable from the UI. */}
                    {editing && (
                        <label className="flex items-center gap-2 rounded-lg border p-3">
                            <input
                                type="checkbox"
                                checked={form.paid_status}
                                onChange={(e) => setForm({ ...form, paid_status: e.target.checked })}
                                className="h-4 w-4"
                                data-testid="year-paid-toggle"
                            />
                            <div>
                                <p className="text-sm font-medium">Marked as paid</p>
                                <p className="text-[11px] text-muted-foreground">Unlocks admin access for this year.</p>
                            </div>
                        </label>
                    )}
                    {/* Iter 81 · Optional per-year photo spec override. */}
                    <div className="rounded-lg border bg-muted/20 p-3">
                        <p className="label-eyebrow text-[11px]">Photo spec override (optional)</p>
                        <p className="text-[11px] text-muted-foreground mb-2">Leave blank to inherit the district default. Set both fields to override for this year only.</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[11px]" htmlFor="year-photo-min-w">Min width (px)</Label>
                                <Input
                                    id="year-photo-min-w"
                                    type="number"
                                    min="0"
                                    value={form.photo_min_width}
                                    onChange={(e) => setForm({ ...form, photo_min_width: e.target.value })}
                                    placeholder="(inherit)"
                                    data-testid="year-photo-min-width-input"
                                />
                            </div>
                            <div>
                                <Label className="text-[11px]" htmlFor="year-photo-min-h">Min height (px)</Label>
                                <Input
                                    id="year-photo-min-h"
                                    type="number"
                                    min="0"
                                    value={form.photo_min_height}
                                    onChange={(e) => setForm({ ...form, photo_min_height: e.target.value })}
                                    placeholder="(inherit)"
                                    data-testid="year-photo-min-height-input"
                                />
                            </div>
                        </div>
                    </div>
                    </div>
                    <DialogFooter className="border-t bg-white px-6 py-3 shrink-0">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="year-save-btn">
                            {saving ? "Saving…" : editing ? "Save changes" : "Create year"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function suggestNextYear(existingYears = []) {
    if (!existingYears.length) return currentRotaryYear();
    const latest = existingYears.map((y) => y.year).sort().slice(-1)[0];
    const m = /^(\d{4})-(\d{2})$/.exec(latest || "");
    if (!m) return currentRotaryYear();
    const start = parseInt(m[1], 10) + 1;
    const end = String((start + 1) % 100).padStart(2, "0");
    return `${start}-${end}`;
}

function currentRotaryYear() {
    const now = new Date();
    const y = now.getFullYear();
    // Rotary year runs July -> June. If month >= July (index 6), current year starts this calendar year.
    const start = now.getMonth() >= 6 ? y : y - 1;
    const end = String((start + 1) % 100).padStart(2, "0");
    return `${start}-${end}`;
}

function yearFormatError(input) {
    const m = /^(\d{4})-(\d{2})$/.exec((input || "").trim());
    if (!m) return "Format: YYYY-YY (e.g., 2026-27)";
    const start = parseInt(m[1], 10);
    const expected = String((start + 1) % 100).padStart(2, "0");
    if (m[2] !== expected) return `Suffix should be "${expected}". Did you mean ${start}-${expected}?`;
    return null;
}

function yearRangeWarning(input) {
    const m = /^(\d{4})-(\d{2})$/.exec((input || "").trim());
    if (!m) return null;
    const inputStart = parseInt(m[1], 10);
    const cur = parseInt(currentRotaryYear().slice(0, 4), 10);
    const delta = inputStart - cur;
    if (delta > 2) return `This is ${delta} year${delta !== 1 ? "s" : ""} in the future. Current Rotary year is ${currentRotaryYear()} — please double-check.`;
    if (delta < -5) return `This is ${Math.abs(delta)} years in the past. Current Rotary year is ${currentRotaryYear()} — please double-check.`;
    return null;
}

/* ================================ Users ================================ */

function UsersSection() {
    const [users, setUsers] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: u }, { data: d }] = await Promise.all([
                api.get("/super/users"),
                api.get("/super/districts"),
            ]);
            setUsers(u);
            setDistricts(d);
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    // Users: fetch once on mount
    useEffect(() => { load(); }, []);

    const del = async (u) => {
        if (!window.confirm(`Delete user "${u.email}"?`)) return;
        try {
            await api.delete(`/super/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail));
        }
    };

    const resendInvite = async (u) => {
        try {
            const { data } = await api.post(`/super/users/${u.id}/resend-invite`);
            if (data.ok) toast.success(`Invite email sent to ${u.email}`);
            else toast.error(`Could not send invite: ${data.detail || "unknown error"}`);
        } catch (e) {
            toast.error(fmtErr(e?.response?.data?.detail));
        }
    };

    const districtMap = useMemo(() => Object.fromEntries(districts.map((d) => [d.id, d])), [districts]);

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="rotary-heading text-2xl text-rotary-ink">Users</h2>
                    <p className="text-sm text-muted-foreground">
                        {users.length} user{users.length !== 1 && "s"} · super admins + district admins
                    </p>
                </div>
                <Button
                    size="sm"
                    onClick={() => setCreateOpen(true)}
                    className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                    data-testid="users-create-btn"
                >
                    <Plus className="mr-1.5 h-4 w-4" /> <span className="hidden sm:inline">New&nbsp;</span>user
                </Button>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
                <div className="overflow-x-auto rounded-xl border bg-white">
                    <table className="w-full min-w-[600px] text-sm">
                        <thead className="bg-muted/30 text-left">
                            <tr>
                                <th className="px-4 py-3 label-eyebrow">Email · Name</th>
                                <th className="px-4 py-3 label-eyebrow">Role · Scope</th>
                                <th className="px-4 py-3 label-eyebrow">Status</th>
                                <th className="px-4 py-3 label-eyebrow">Last login</th>
                                <th className="px-4 py-3 label-eyebrow"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => {
                                const d = u.district_id ? districtMap[u.district_id] : null;
                                return (
                                    <tr key={u.id} className="border-t" data-testid={`user-row-${u.email}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium">{u.email}</p>
                                            <p className="text-xs text-muted-foreground">{u.name}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium capitalize">{u.role.replace("_", " ")}</p>
                                                {u.role === "super_admin" && (
                                                    <span className="rotary-chip text-[10px] bg-rotary-gold/15 text-rotary-ink border-rotary-gold/40">
                                                        Full platform
                                                    </span>
                                                )}
                                            </div>
                                            {(u.scopes && u.scopes.length > 0) ? (
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {u.scopes.map((s) => (
                                                        <span key={`${s.district_id || s.district_number}-${s.year}`} className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-700">
                                                            RID {s.district_number} · {s.year}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : d && (
                                                <p className="text-xs text-muted-foreground">
                                                    RID {d.district_number} · {u.year}
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`rotary-chip text-[11px] ${
                                                u.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                                u.status === "read_only" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                                "bg-red-50 text-red-700 border-red-200"
                                            }`}>{u.status}</span>
                                            {u.must_reset_password && <p className="mt-1 text-[10px] text-amber-700">Reset pending</p>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "Never"}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => resendInvite(u)} data-testid={`user-invite-${u.email}`} title="Resend invite email (fresh reset link)">
                                                    <Send className="h-4 w-4 text-rotary-blue" />
                                                </Button>
                                                {u.role !== "super_admin" && (
                                                    <Button variant="ghost" size="sm" onClick={() => setEditing(u)} data-testid={`user-edit-${u.email}`}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => del(u)} data-testid={`user-delete-${u.email}`} title="Delete user">
                                                    <Trash2 className="h-4 w-4 text-rotary-cranberry" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <UserModal
                open={createOpen}
                districts={districts}
                onOpenChange={setCreateOpen}
                onSaved={() => { setCreateOpen(false); load(); }}
            />
            <UserModal
                open={!!editing}
                user={editing}
                districts={districts}
                onOpenChange={(o) => !o && setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
            />
        </div>
    );
}

/* ============================ Audit Log ============================ */

function AuditLogSection() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ action_prefix: "", actor_email: "" });
    const [page, setPage] = useState(0);
    const PAGE = 50;

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("limit", String(PAGE));
            params.set("skip", String(page * PAGE));
            if (filter.action_prefix) params.set("action_prefix", filter.action_prefix);
            if (filter.actor_email) params.set("actor_email", filter.actor_email);
            const { data } = await api.get(`/super/audit-log?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) { toast.error(fmtErr(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    // Audit log: refetch when page changes; filters trigger explicit reloads via Apply/Clear.
    useEffect(() => { load(); }, [page]);

    const applyFilters = () => { setPage(0); load(); };
    const clearFilters = () => { setFilter({ action_prefix: "", actor_email: "" }); setPage(0); setTimeout(load, 0); };

    const actionColor = (action) => {
        if (action.endsWith(".create")) return "text-emerald-700 bg-emerald-50 border-emerald-200";
        if (action.endsWith(".delete")) return "text-rotary-cranberry bg-red-50 border-red-200";
        if (action.endsWith(".update")) return "text-rotary-blue bg-blue-50 border-blue-200";
        if (action.includes("resend")) return "text-amber-800 bg-amber-50 border-amber-200";
        return "text-slate-700 bg-slate-50 border-slate-200";
    };

    return (
        <div className="space-y-4">
            <div className="rounded-lg border bg-white p-4">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[200px]">
                        <Label className="label-eyebrow">Action starts with</Label>
                        <Input
                            value={filter.action_prefix}
                            onChange={(e) => setFilter({ ...filter, action_prefix: e.target.value })}
                            placeholder="e.g. user. or district."
                            data-testid="audit-filter-action"
                        />
                    </div>
                    <div className="min-w-[240px] flex-1">
                        <Label className="label-eyebrow">Actor email</Label>
                        <Input
                            value={filter.actor_email}
                            onChange={(e) => setFilter({ ...filter, actor_email: e.target.value })}
                            placeholder="exact email match"
                            data-testid="audit-filter-actor"
                        />
                    </div>
                    <Button onClick={applyFilters} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="audit-apply-btn">Apply</Button>
                    <Button variant="outline" onClick={clearFilters} className="rounded-full">Clear</Button>
                </div>
            </div>

            <div className="rounded-lg border bg-white overflow-hidden">
                <div className="flex items-center justify-between border-b px-4 py-2 text-xs text-muted-foreground">
                    <span data-testid="audit-total">Total {total}</span>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
                        <span>Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
                        <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
                    </div>
                </div>
                {loading ? (
                    <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="p-8 text-center text-sm text-muted-foreground">No entries yet.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-4 py-2 text-left">When</th>
                                <th className="px-4 py-2 text-left">Actor</th>
                                <th className="px-4 py-2 text-left">Action</th>
                                <th className="px-4 py-2 text-left">Target</th>
                                <th className="px-4 py-2 text-left">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {items.map((it) => {
                                const ts = it.timestamp?.replace("T", " ").slice(0, 19);
                                const detailKeys = Object.keys(it.details || {});
                                return (
                                    <tr key={it.id} data-testid={`audit-row-${it.id}`} className="align-top">
                                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{ts}</td>
                                        <td className="px-4 py-3">
                                            <p className="text-xs">{it.actor_email}</p>
                                            {it.request_ip && <p className="text-[10px] text-muted-foreground">{it.request_ip}</p>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${actionColor(it.action)}`}>
                                                {it.action}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            <p className="font-medium">{it.resource_label || "—"}</p>
                                            {it.resource_type && <p className="text-[10px] text-muted-foreground">{it.resource_type}</p>}
                                        </td>
                                        <td className="px-4 py-3 text-[11px] text-muted-foreground max-w-md truncate" title={JSON.stringify(it.details)}>
                                            {detailKeys.length ? detailKeys.map((k) => `${k}: ${typeof it.details[k] === "object" ? "…" : it.details[k]}`).join(" · ") : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}


function UserModal({ open, onOpenChange, onSaved, districts = [], user }) {
    const editing = !!user;
    const [form, setForm] = useState({
        email: "", name: "", role: "district_admin",
        scopes: [{ district_id: "", year: "" }],
        temp_password: "", send_invite: true,
        status: "active", reset_password: "",
    });
    const [saving, setSaving] = useState(false);
    // Stable client-side keys per scope row so removing a middle row doesn't shuffle React state
    const scopeKeysRef = useRef([]);
    while (scopeKeysRef.current.length < form.scopes.length) scopeKeysRef.current.push(`sc-${Math.random().toString(36).slice(2, 10)}`);
    if (scopeKeysRef.current.length > form.scopes.length) scopeKeysRef.current.length = form.scopes.length;

    useEffect(() => {
        if (open) {
            let initialScopes = [{ district_id: "", year: "" }];
            if (user?.scopes && user.scopes.length > 0) {
                initialScopes = user.scopes.map((s) => ({ district_id: s.district_id, year: s.year }));
            } else if (user?.district_id && user?.year) {
                initialScopes = [{ district_id: user.district_id, year: user.year }];
            }
            setForm({
                email: user?.email || "",
                name: user?.name || "",
                role: user?.role || "district_admin",
                scopes: initialScopes,
                temp_password: "", send_invite: true,
                status: user?.status || "active",
                reset_password: "",
            });
        }
    }, [open, user]);

    const yearsForDistrict = (did) => (districts.find((d) => d.id === did)?.years) || [];

    const updateScope = (i, patch) => {
        const next = [...form.scopes];
        next[i] = { ...next[i], ...patch };
        // Reset year when district changes
        if ("district_id" in patch) next[i].year = "";
        setForm({ ...form, scopes: next });
    };

    const addScope = () => setForm({ ...form, scopes: [...form.scopes, { district_id: "", year: "" }] });
    const removeScope = (i) => {
        scopeKeysRef.current.splice(i, 1);
        setForm({ ...form, scopes: form.scopes.filter((_, idx) => idx !== i) });
    };

    const submit = async (e) => {
        e.preventDefault();
        const isSuper = form.role === "super_admin";
        // Validate scopes only for district_admin
        const cleanScopes = form.scopes.filter((s) => s.district_id && s.year);
        if (!isSuper && cleanScopes.length === 0) {
            toast.error("Assign at least one district + year scope");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                const payload = {
                    name: form.name,
                    status: form.status,
                    scopes: cleanScopes,
                };
                if (form.reset_password) payload.reset_password = form.reset_password;
                await api.patch(`/super/users/${user.id}`, payload);
                toast.success("User updated");
            } else {
                if (form.temp_password && form.temp_password.length < 8) {
                    toast.error("If provided, temporary password must be at least 8 characters");
                    setSaving(false);
                    return;
                }
                const { data } = await api.post("/super/users", {
                    email: form.email.trim(),
                    name: form.name.trim(),
                    role: form.role,
                    scopes: isSuper ? [] : cleanScopes,
                    temp_password: form.temp_password,
                    send_invite: form.send_invite,
                });
                if (form.send_invite) {
                    if (data.invite_sent) toast.success(`Invite email sent to ${data.email}`);
                    else toast.warning(`User created, but invite email failed: ${data.invite_detail || "unknown"}`, { duration: 8000 });
                } else {
                    toast.success(isSuper ? "Super admin created" : "District admin created");
                }
            }
            onSaved?.();
        } catch (e2) {
            toast.error(fmtErr(e2?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl">
                        {editing ? "Edit user" : (form.role === "super_admin" ? "New super admin" : "New district admin")}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    {!editing && (
                        <div>
                            <Label className="label-eyebrow">Role *</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="district_admin">District admin — access to specific district+year</SelectItem>
                                    <SelectItem value="super_admin">Super admin — full platform access</SelectItem>
                                </SelectContent>
                            </Select>
                            {form.role === "super_admin" && (
                                <p className="mt-1 text-[11px] text-amber-700">
                                    Super admins can create/delete users, districts, and years across the whole platform. Grant sparingly.
                                </p>
                            )}
                        </div>
                    )}
                    <div>
                        <Label className="label-eyebrow">Email *</Label>
                        <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="admin@d3192.org"
                            disabled={editing}
                            data-testid="user-email-input"
                            required
                        />
                    </div>
                    <div>
                        <Label className="label-eyebrow">Full name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Rtn. Priya Iyer"
                            data-testid="user-name-input"
                        />
                    </div>

                    {(editing || form.role === "district_admin") && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="label-eyebrow">Assigned scopes *</Label>
                            <Button type="button" variant="ghost" size="sm" onClick={addScope} data-testid="user-add-scope-btn" className="h-7 px-2 text-xs">
                                <Plus className="mr-1 h-3 w-3" /> Add another year
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {form.scopes.map((s, i) => (
                                <div key={scopeKeysRef.current[i]} className="flex items-start gap-2" data-testid={`user-scope-row-${i}`}>
                                    <Select value={s.district_id} onValueChange={(v) => updateScope(i, { district_id: v })}>
                                        <SelectTrigger className="flex-1" data-testid={`user-scope-district-${i}`}><SelectValue placeholder="District" /></SelectTrigger>
                                        <SelectContent>
                                            {districts.map((d) => (
                                                <SelectItem key={d.id} value={d.id}>RID {d.district_number}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select value={s.year} onValueChange={(v) => updateScope(i, { year: v })} disabled={!s.district_id}>
                                        <SelectTrigger className="flex-1" data-testid={`user-scope-year-${i}`}>
                                            <SelectValue placeholder={s.district_id ? "Year" : "Pick district"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {yearsForDistrict(s.district_id).map((y) => (
                                                <SelectItem key={y.id} value={y.year}>{y.year}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {form.scopes.length > 1 && (
                                        <Button type="button" variant="ghost" size="sm" className="mt-0.5 h-9 w-9 p-0" onClick={() => removeScope(i)} data-testid={`user-scope-remove-${i}`}>
                                            <Trash2 className="h-3.5 w-3.5 text-rotary-cranberry" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <p className="mt-2 text-[11px] text-muted-foreground">
                            {form.scopes.length > 1
                                ? "This admin will see a year switcher and can move between assigned scopes."
                                : "Add more scopes to give one admin access to multiple years."}
                        </p>
                    </div>
                    )}

                    {editing ? (
                        <>
                            <div>
                                <Label className="label-eyebrow">Status</Label>
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="active">Active</SelectItem>
                                        <SelectItem value="read_only">Read-only</SelectItem>
                                        <SelectItem value="disabled">Disabled</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="label-eyebrow">Reset password (optional)</Label>
                                <Input
                                    type="text"
                                    value={form.reset_password}
                                    onChange={(e) => setForm({ ...form, reset_password: e.target.value })}
                                    placeholder="Leave blank to keep existing"
                                    data-testid="user-reset-password-input"
                                />
                                <p className="mt-1 text-[11px] text-muted-foreground">If set, the user will be forced to reset on next login.</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <label className="flex items-start gap-3 rounded-lg border bg-slate-50 p-3 cursor-pointer" data-testid="user-send-invite-toggle">
                                <input
                                    type="checkbox"
                                    checked={form.send_invite}
                                    onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                                    className="mt-0.5"
                                />
                                <div>
                                    <p className="text-sm font-medium">Send invite email</p>
                                    <p className="text-[11px] text-muted-foreground">Emails a password-setup link (valid 48 hours). Recommended.</p>
                                </div>
                            </label>
                            {!form.send_invite && (
                                <div>
                                    <Label className="label-eyebrow">Temporary password (optional)</Label>
                                    <Input
                                        type="text"
                                        value={form.temp_password}
                                        onChange={(e) => setForm({ ...form, temp_password: e.target.value })}
                                        placeholder="min 8 chars — share with admin"
                                        data-testid="user-temp-password-input"
                                    />
                                    <p className="mt-1 text-[11px] text-muted-foreground">Leave blank to generate one automatically. Admin will be forced to reset on first login.</p>
                                </div>
                            )}
                        </>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky" data-testid="user-save-btn">
                            {saving ? "Saving…" : editing ? "Save changes" : "Create admin"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
